import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import useCallbackCall from '@/hoc/hooks/useCallbackCall'
import useCallbackReminders, { CallbackReminder, CallbackReminderId } from '@/hoc/hooks/useCallbackReminders'
import useNow from '@/shared/hooks/useNow'
import { formatCallbackTime, isCallbackExpired, minutesUntilCallback } from './callbackReminderDates'
import useCallbackReminderSpace from './useCallbackReminderSpace'

export interface CallbackReminderCardView {
  id: CallbackReminderId
  title: string
  subtitle: string
  customer: string
  phone: string
  hasCustomerDetails: boolean
  isLate: boolean
  hasContact: boolean
  isDismissing: boolean
  canCall: boolean
  isCalling: boolean
  callStatus: string
  callFailed: boolean
  closeLabel: string
  openContactLabel: string
  callLabel: string
  onClose: () => void
  onOpenContact: () => void
  onCall: () => void
}

interface UseCallbackReminderPopupReturn {
  cards: CallbackReminderCardView[]
  stackLabel: string
  stackRef: (stack: HTMLElement | null) => void
}

const reminderTitle = (t: TFunction, scheduledAt: string, minutesLeft: number, isLate: boolean): string => {
  if (isLate)
    return t('subscriptionTools:schedule_callback.reminder_popup.title_late', { time: formatCallbackTime(scheduledAt) })

  return t('subscriptionTools:schedule_callback.reminder_popup.title_countdown', { count: minutesLeft })
}

const customerName = (reminder: CallbackReminder): string =>
  `${reminder.contact_first_name ?? ''} ${reminder.contact_last_name ?? ''}`.trim()

const useCallbackReminderPopup = (): UseCallbackReminderPopupReturn => {
  const { t } = useTranslation()
  const { reminders, dismissingIds, dismiss } = useCallbackReminders()
  const callback = useCallbackCall()
  const now = useNow(reminders.length > 0)
  const stackRef = useCallbackReminderSpace()

  const cards = reminders
    .filter(reminder => !isCallbackExpired(reminder.scheduled_at, now))
    .map(reminder => {
      const minutesLeft = minutesUntilCallback(reminder.scheduled_at, now)
      const isLate = !(minutesLeft > 0)
      const customer = customerName(reminder)
      const phone = reminder.contact_phone ?? ''

      return {
        id: reminder.id,
        title: reminderTitle(t, reminder.scheduled_at, minutesLeft, isLate),
        subtitle: t('subscriptionTools:schedule_callback.reminder_popup.scheduled_at', {
          time: formatCallbackTime(reminder.scheduled_at),
        }),
        customer,
        phone,
        hasCustomerDetails: Boolean(customer || phone),
        isLate,
        hasContact: Boolean(reminder.zoho_contact_id),
        isDismissing: dismissingIds.has(reminder.id),
        canCall: callback.enabled && Boolean(reminder.contact_phone),
        isCalling: callback.callingIds.has(reminder.id),
        callStatus: callback.statuses.get(reminder.id)?.message ?? '',
        callFailed: callback.statuses.get(reminder.id)?.failed ?? false,
        closeLabel: t('subscriptionTools:schedule_callback.reminder_popup.close'),
        openContactLabel: t('subscriptionTools:schedule_callback.reminder_popup.open_contact'),
        callLabel: t('subscriptionTools:schedule_callback.reminder_popup.call'),
        onClose: () => {
          dismiss(reminder.id)
        },
        onOpenContact: () => {
          dismiss(reminder.id)
          window.open(`/contact/${reminder.zoho_contact_id}`, '_blank')
        },
        onCall: () => {
          callback.call(reminder.id)
        },
      }
    })

  return { cards, stackLabel: t('subscriptionTools:schedule_callback.reminder_popup.stack_label'), stackRef }
}

export default useCallbackReminderPopup
